import { BaseService } from "./common/base";

/** What became of the shadow checks of one repository method. */
export type ShadowMethodReport = {
  method: string;
  kind: "read" | "write";
  same?: number;
  differs?: number;
  "too-large"?: number;
  lagging?: number;
  timeout?: number;
  failed?: number;
  skipped?: number;
  "dgraph-failed"?: number;
  "copy-failed"?: number;
  /** same / (same + differs), null when nothing was compared */
  agreement: number | null;
};

export type ShadowReport = {
  backend: "dgraph" | "shadow" | "postgres";
  /** when the reporting API instance started: counts are since then */
  since: string;
  methods: ShadowMethodReport[];
};

export type AdminUserType =
  | "patient"
  | "doctor"
  | "assistant"
  | "organization"
  | "organizationManager"
  | "midwife"
  | "dispatchManager"
  | "dispatchOperator"
  | "admin";

/** One line of `GET /admin/users`. */
export type AdminUser = {
  id: string;
  username: string | null;
  userType: AdminUserType | null;
  creation: string | null;
  /** Profile name(s), joined when the user has several profiles. */
  name: string | null;
  /** One of the profiles is active. */
  active: boolean;
  /** A deletion of the account is scheduled. */
  deletionPending: boolean;
  /** Establishments of a professional; always empty for a patient. */
  organizations: Array<{ id: string; name: string | null }>;
};

export type AdminUserSort = "creation_desc" | "creation_asc" | "name";

/** Filters of the user list, shared by the list and its CSV export. */
export type AdminUserFilters = {
  search?: string;
  userType?: AdminUserType;
  organizationId?: string;
  active?: boolean;
  /** Created at or after that instant (ISO). */
  createdFrom?: string;
  /** Created strictly before that instant (ISO). */
  createdTo?: string;
  deletionPending?: boolean;
  sort?: AdminUserSort;
};

export type AdminUserList = { total: number; users: AdminUser[] };

/**
 * One line of `GET /admin/appointments`: when, what kind, status history,
 * who gave it and where. Of the patient, only whether there is one.
 */
export type AdminAppointment = {
  id: string;
  start: string | null;
  end: string | null;
  created: string | null;
  status: string | null;
  serviceCategory: number | null;
  serviceType: number | null;
  specialty: number | null;
  class: "HH" | "VR" | "AS" | null;
  virtual: boolean | null;
  hasPatient: boolean;
  professionals: Array<{
    id: string;
    role: "doctor" | "midwife" | "assistant";
    name: string | null;
    rpps: string | null;
  }>;
  organizations: Array<{ id: string; name: string | null }>;
  events: Array<{ type: string | null; date: string | null }>;
  paymentStatus: string | null;
};

/** Filters of `GET /admin/appointments`; `from` and `to` bound the start. */
export type AdminAppointmentQuery = {
  status?: string;
  from?: string;
  to?: string;
  organizationId?: string;
  practitionerId?: string;
  first?: number;
  offset?: number;
};

export type AccountDeletionStatus =
  | "PENDING"
  | "PURGING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export type AccountDeletionStep = {
  step: string;
  outcome: "DONE" | "SKIPPED" | "PARTIAL" | "FAILED";
  detail?: string;
  at: string;
};

/** A pending or failed erasure, from `GET /admin/account-deletions`. */
export type AccountDeletion = {
  id: string;
  userRef?: string;
  userType?: string;
  reason?: "SELF_SERVICE" | "ADMIN";
  status: AccountDeletionStatus;
  requestedAt: string;
  scheduledFor: string;
  completedAt?: string;
  attempts?: number;
  /** JSON array of AccountDeletionStep */
  steps?: string;
};

/** A completed or cancelled erasure, from `GET /admin/account-deletions/history`. */
export type AccountDeletionHistoryEntry = {
  id: string;
  /** Only while it was not cleared: a completed erasure carries none. */
  userRef?: string;
  userType?: string;
  reason?: "SELF_SERVICE" | "ADMIN";
  status: "COMPLETED" | "CANCELLED";
  requestedAt: string;
  completedAt?: string;
};

export type AccountDeletionHistoryPage = {
  total: number;
  records: AccountDeletionHistoryEntry[];
};

export type AccountDeletionResult = {
  id: string;
  status: AccountDeletionStatus | "PARTIAL";
  steps?: AccountDeletionStep[];
};

export type ApiHealth = {
  uptime: number;
  time: number;
  status: string;
  subServices: Record<string, ApiCheckStatus>;
};

export type ApiCheckStatus = "ok" | "error" | "not-configured";

/** One check of `GET /admin/status`, with its in-memory 24 h history. */
export type ApiStatusCheck = {
  id: string;
  name: string;
  group: "infra" | "third-party";
  /** call: an API call; reachability: the host answers; config: setting present, nothing called */
  kind: "call" | "reachability" | "config";
  /** a failure makes the monitoring healthcheck answer 503 */
  critical: boolean;
  status: ApiCheckStatus;
  latencyMs: number | null;
  error?: string;
  note?: string;
  checkedAt: string;
  /** ok / (ok + error) over 24 h, null without samples */
  availability24h: number | null;
  samples24h: number;
  failures24h: number;
  lastFailureAt: string | null;
  lastFailureError: string | null;
  lastSuccessAt: string | null;
};

export type ApiStatus = {
  api: {
    version: string | null;
    commit: string | null;
    nodeEnv: string | null;
    databaseBackend: "dgraph" | "shadow" | "postgres";
    startedAt: string;
    uptime: number;
  };
  checkedAt: string;
  /** the history lives in the API process memory */
  historyResetsOnRestart: boolean;
  checks: ApiStatusCheck[];
};

/** What support sees of a user, from `GET /admin/users/:id` (no health data). */
export type AdminUserFile = {
  id: string;
  username: string | null;
  userType: AdminUserType | null;
  creation: string | null;
  account: {
    timeJoined: number;
    loginMethods: Array<{
      method: string;
      email?: string;
      phoneNumber?: string;
      verified: boolean;
      timeJoined: number;
    }>;
  } | null;
  profile: {
    id: string | null;
    active: boolean | null;
    name: string | null;
    rpps: string | null;
    siret: string | null;
    telecom: Array<{ system: string | null; value: string | null }>;
    organizations: Array<{ id: string; name: string | null }>;
  } | null;
  appointments: Array<{
    id: string;
    start: string | null;
    end: string | null;
    status: string | null;
    serviceType: string | null;
  }>;
  deletion: {
    status: AccountDeletionStatus;
    requestedAt: string;
    scheduledFor: string;
  } | null;
  /** Connected services: type and expiry only, never tokens. */
  integrations?: Array<{
    type: string | null;
    state: AdminIntegrationState;
    expiresAt: string | null;
  }>;
};

/** `expiring`: within 7 days; `active` includes those without expiry. */
export type AdminIntegrationState = "active" | "expired" | "expiring";

/** Counts of one integration type, from `GET /admin/integrations/summary`. */
export type AdminIntegrationTypeSummary = {
  type: string;
  total: number;
  expired: number;
  expiring: number;
  withoutExpiry: number;
};

/** One integration, from `GET /admin/integrations`: no token, no meta value. */
export type AdminIntegration = {
  id: string;
  type: string | null;
  scope: string | null;
  expiresAt: string | null;
  state: AdminIntegrationState;
  /** A refresh token is stored (the token itself is never sent). */
  refreshable: boolean;
  /** Top-level keys of the integration's settings, without their values. */
  metaKeys: string[];
  user: {
    id: string;
    username: string | null;
    userType: AdminUserType | null;
  } | null;
};

export type AdminIntegrationPage = {
  total: number;
  integrations: AdminIntegration[];
};

export type NotificationCounts = {
  total: number;
  sent: number;
  failed: number;
  pending: number;
};

/** Novu delivery counts, from `GET /admin/notifications/summary`. */
export type NotificationsSummary = {
  days: number;
  since: string;
  total: number;
  /** Novu's list was cut before the start of the period: counts are partial. */
  truncated: boolean;
  scannedAt: string;
  channels: Record<string, NotificationCounts>;
  statuses: { sent: number; failed: number; pending: number };
  topWorkflows: Array<{ workflow: string; total: number; failed: number }>;
};

/** A failed delivery: the subscriber id is a user id; errors are sanitized. */
export type NotificationFailure = {
  at: string;
  channel: string;
  workflow: string;
  subscriberId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
};

export type NotificationFailures = {
  scannedAt: string;
  truncated: boolean;
  failures: NotificationFailure[];
};

/** One admin action, from `GET /admin/audit`. */
export type AdminAuditEvent = {
  id: string;
  at: string;
  actorRef: string;
  actorLabel?: string;
  action: string;
  targetType?: string;
  targetRef?: string;
  reason?: string;
  /** JSON without health data */
  details?: string;
};

export type AdminAuditPage = { total: number; events: AdminAuditEvent[] };

/** Filters of the audit trail, shared by the list and its CSV export. */
export type AdminAuditFilters = {
  actorRef?: string;
  targetRef?: string;
  action?: string;
  /** At or after that instant (ISO). */
  from?: string;
  /** Strictly before that instant (ISO). */
  to?: string;
};

/** A downloaded file and the name the API gave it. */
export type DownloadedFile = { blob: Blob; filename: string };

/** The file name of a Content-Disposition header, or the fallback. */
export const filenameOf = (disposition: unknown, fallback: string) =>
  (typeof disposition === "string" &&
    /filename="?([^";]+)"?/.exec(disposition)?.[1]) ||
  fallback;
export type AdminProfessionalType = "doctor" | "midwife" | "assistant";

/** One line of `GET /admin/professionals`: identity and slot counts only. */
export type AdminProfessional = {
  type: AdminProfessionalType;
  id: string;
  userId: string | null;
  username: string | null;
  name: string | null;
  rpps: string | null;
  siret: string | null;
  active: boolean | null;
  qualifications: Array<{ code: string | null; system: string | null }>;
  organizations: Array<{ id: string; name: string | null }>;
  ordoclic: boolean;
  hasSchedule: boolean;
  /** Minutes open per week in the schedule settings. */
  weeklyMinutes: number;
  freeSlots7d: number | null;
  freeSlots30d: number | null;
  lastSlotEnd: string | null;
  duplicateRpps: boolean;
};

export type AdminProfessionalList = {
  total: number;
  professionals: AdminProfessional[];
};

export type AdminProfessionalWeek = {
  start: string;
  end: string;
  free: number;
  booked: number;
  unavailable: number;
};

/** `GET /admin/professionals/:type/:id`; each opening is audited. */
export type AdminProfessionalDetail = AdminProfessional & {
  addresses: Array<{
    line: string[];
    postalCode: string | null;
    city: string | null;
    country: string | null;
    text: string | null;
    use: string | null;
  }>;
  telecom: Array<{
    system: string | null;
    value: string | null;
    use: string | null;
  }>;
  schedule: {
    slotDuration: number | null;
    /** 1 = Monday … 7 = Sunday; times as dates of the week of 2018-01-01. */
    weeklyPattern: Array<{ weekday: number; start: string; end: string }>;
  } | null;
  weeks: AdminProfessionalWeek[];
  pastAppointments: number;
  sameRpps: Array<{
    type: AdminProfessionalType;
    id: string;
    name: string | null;
  }>;
};

/** Service requests of one status, counted once each. */
export type AdminStatusCount = { status: string; count: number };

/** One line of `GET /admin/dispatch/organizations`. */
export type AdminDispatchOrganizationSummary = {
  id: string;
  name: string | null;
  active: boolean | null;
  managersCount: number;
  operatorsCount: number;
  doctorsCount: number;
  assistantsCount: number;
  inventory: { id: string; itemsCount: number; outOfStockCount: number } | null;
  serviceRequests: AdminStatusCount[];
};

export type AdminDispatchMember = {
  id: string;
  name: string | null;
  email: string | null;
  userId: string | null;
  active: boolean | null;
};

/** `GET /admin/dispatch/organizations/:id` (no patient data). */
export type AdminDispatchOrganization = {
  id: string;
  name: string | null;
  active: boolean | null;
  managers: AdminDispatchMember[];
  operators: AdminDispatchMember[];
  professionals: Array<{
    id: string;
    type: "Doctor" | "Assistant";
    name: string | null;
  }>;
  inventory: {
    id: string;
    items: Array<{
      id: string;
      productId: string | null;
      product: string | null;
      baseUnit: string | null;
      quantity: number;
      /** Not in the data model yet: low stock is only flagged when set. */
      threshold?: number | null;
    }>;
  } | null;
  /** Latest first; a delivery during an appointment carries its date only. */
  deliveries: Array<{
    id: string;
    date: string | null;
    product: string | null;
    baseUnit: string | null;
    quantity: number;
  }>;
  serviceRequests: AdminStatusCount[];
};

export type AdminPaymentStatus =
  | "QUEUED"
  | "COMPLETE"
  | "ERROR"
  | "PARTIAL"
  | "NONE";

/** One payment of `GET /admin/payments` (no patient, no appointment content). */
export type AdminPayment = {
  id: string;
  date: string | null;
  created: string | null;
  /** In the currency's minor unit (cents for EUR). */
  amount: { value: number | null; currency: string | null } | null;
  status: string | null;
  outcome: string | null;
  kind: string | null;
  method: string | null;
  requestor: {
    type: "Doctor" | "Assistant" | "Midwife";
    id: string;
    name: string | null;
  } | null;
  appointment: { id: string; start: string | null } | null;
  processor: string | null;
  stripePaymentIntentId: string | null;
};

export type AdminPaymentPage = {
  total: number;
  payments: AdminPayment[];
  totals: Array<{
    status: string;
    count: number;
    amounts: Array<{ currency: string; value: number }>;
  }>;
};

export type AdminOrganizationRef = { id: string; name: string | null };

type AdminOrganizationBase = {
  id: string;
  name: string | null;
  /** Comma separated specialities, as chosen at sign-up. */
  type: string | null;
  active: boolean;
  finess: string | null;
  siret: string | null;
  city: string | null;
  /** The primary manager's user. */
  manager: { id: string; email: string | null } | null;
  parent: AdminOrganizationRef | null;
};

/** One line of `GET /admin/organizations`. */
export type AdminOrganizationSummary = AdminOrganizationBase & {
  counts: {
    coManagers: number;
    /** Doctors, midwives and assistants. */
    practitioners: number;
    services: number;
    children: number;
  };
};

export type AdminOrganizationPage = {
  total: number;
  organizations: AdminOrganizationSummary[];
};

/** An organization's page, `GET /admin/organizations/:id`: no patient data. */
export type AdminOrganizationDetail = AdminOrganizationBase & {
  address: Array<{
    id: string;
    text: string | null;
    line: string[];
    postalCode: string | null;
    city: string | null;
    country: string | null;
  }>;
  telecom: Array<{ id: string; system: string | null; value: string | null }>;
  managers: Array<{
    id: string;
    userId: string | null;
    email: string | null;
    name: string | null;
    primary: boolean;
  }>;
  practitioners: Array<{
    id: string;
    userId: string | null;
    type: "doctor" | "midwife" | "assistant";
    name: string | null;
    rpps: string | null;
    active: boolean | null;
  }>;
  services: Array<{
    id: string;
    name: string | null;
    type: string | null;
    category: string | null;
    active: boolean | null;
  }>;
  children: Array<AdminOrganizationRef & { active: boolean }>;
  locations: Array<{
    id: string;
    name: string | null;
    type: string | null;
    status: string | null;
    city: string | null;
  }>;
  counts: AdminOrganizationSummary["counts"] & {
    patients: number;
    transmissions: number;
  };
};

export class AdminService extends BaseService {
  constructor() {
    super("admin");
  }

  getAppointmentStatistics(params: {
    periodStartDate: Date;
    periodEndDate: Date;
  }) {
    return this._get("/appointments/statistics", {
      params,
    });
  }

  /** Appointments latest first, with no patient identity nor health data. */
  findAppointments(
    params?: AdminAppointmentQuery
  ): Promise<{ data: AdminAppointment[] }> {
    return this._get("/appointments", {
      params,
    });
  }

  getShadowReport(): Promise<ShadowReport> {
    return this._get("/shadow-report");
  }

  findUsers(
    params: AdminUserFilters & { first?: number; offset?: number }
  ): Promise<AdminUserList> {
    return this._get("/users", { params });
  }

  /** The users matching the filters as CSV; the export is audited. */
  exportUsers(params: AdminUserFilters): Promise<DownloadedFile> {
    return this.download("/users/export.csv", params, "utilisateurs.csv");
  }

  private download(
    url: string,
    params: object,
    fallback: string
  ): Promise<DownloadedFile> {
    return this.api.get(url, { params, responseType: "blob" }).then((res) => ({
      blob: res.data,
      filename: filenameOf(res.headers?.["content-disposition"], fallback),
    }));
  }

  /** Immediate and irreversible erasure of the account; the reason is audited. */
  deleteUser(id: string, reason?: string): Promise<AccountDeletionResult> {
    return this._delete(`/users/${id}`, {
      params: reason ? { reason } : undefined,
    });
  }

  /** The user's file; each opening is written to the audit trail. */
  getUserFile(id: string, reason?: string): Promise<AdminUserFile> {
    return this._get(`/users/${id}`, {
      params: reason ? { reason } : undefined,
    });
  }

  findAudit(
    params: AdminAuditFilters & { first?: number; offset?: number }
  ): Promise<AdminAuditPage> {
    return this._get("/audit", { params });
  }

  /** The audit trail matching the filters as CSV; the export is audited. */
  exportAudit(params: AdminAuditFilters): Promise<DownloadedFile> {
    return this.download("/audit/export.csv", params, "audit.csv");
  }

  findProfessionals(params: {
    type?: AdminProfessionalType;
    search?: string;
    active?: boolean;
    organizationId?: string;
    hasSchedule?: boolean;
    first?: number;
    offset?: number;
  }): Promise<AdminProfessionalList> {
    return this._get("/professionals", { params });
  }

  /** A professional's page; each opening is written to the audit trail. */
  getProfessional(
    type: AdminProfessionalType,
    id: string
  ): Promise<AdminProfessionalDetail> {
    return this._get(`/professionals/${type}/${id}`);
  }

  getIntegrationsSummary(): Promise<AdminIntegrationTypeSummary[]> {
    return this._get("/integrations/summary");
  }

  findIntegrations(params: {
    type?: string;
    state?: AdminIntegrationState;
    first?: number;
    offset?: number;
  }): Promise<AdminIntegrationPage> {
    return this._get("/integrations", { params });
  }

  getNotificationsSummary(days?: number): Promise<NotificationsSummary> {
    return this._get("/notifications/summary", {
      params: days ? { days } : undefined,
    });
  }

  findNotificationFailures(first?: number): Promise<NotificationFailures> {
    return this._get("/notifications/failures", {
      params: first ? { first } : undefined,
    });
  }

  findAccountDeletions(): Promise<AccountDeletion[]> {
    return this._get("/account-deletions");
  }

  /** Completed and cancelled erasures, latest request first. */
  findAccountDeletionHistory(params: {
    first?: number;
    offset?: number;
  }): Promise<AccountDeletionHistoryPage> {
    return this._get("/account-deletions/history", { params });
  }

  getHealth(): Promise<ApiHealth> {
    return this._get("/health");
  }

  getStatus(): Promise<ApiStatus> {
    return this._get("/status");
  }

  findSupplyRequests() {
    return this._get("supply-requests");
  }

  getSupplyRequests(id: string) {
    return this._get(`supply-requests/${id}`);
  }

  findDispatchOrganizations(): Promise<AdminDispatchOrganizationSummary[]> {
    return this._get("/dispatch/organizations");
  }

  getDispatchOrganization(id: string): Promise<AdminDispatchOrganization> {
    return this._get(`/dispatch/organizations/${id}`);
  }

  findPayments(params: {
    status?: AdminPaymentStatus;
    from?: string;
    to?: string;
    first?: number;
    offset?: number;
  }): Promise<AdminPaymentPage> {
    return this._get("/payments", { params });
  }

  findOrganizations(params: {
    search?: string;
    active?: boolean;
    type?: string;
    first?: number;
    offset?: number;
  }): Promise<AdminOrganizationPage> {
    return this._get("/organizations", { params });
  }

  getOrganization(id: string): Promise<AdminOrganizationDetail> {
    return this._get(`/organizations/${id}`);
  }

  /** Validates a pending organization; the decision is audited. */
  acceptOrganization(
    id: string,
    reason?: string
  ): Promise<{ id: string; active: true }> {
    return this._post(`/organizations/${id}/accept`, { reason });
  }

  /** Refuses a pending organization, which deletes it; audited first. */
  declineOrganization(
    id: string,
    reason?: string
  ): Promise<{ id: string; deleted: true }> {
    return this._post(`/organizations/${id}/decline`, { reason });
  }
}
